import { Button } from "@designs-by-maryam/ui/components/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@designs-by-maryam/ui/components/select";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { motion } from "motion/react";
import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { z } from "zod";
import { ProjectsGrid } from "@/components/projects/projects-grid";
import { useSafeMotion } from "@/lib/motion";
import { listProjectsInfiniteQueryOptions } from "@/lib/query-options";

const ProjectDetailModal = lazy(() =>
	import("@/components/projects/project-detail-modal").then((m) => ({
		default: m.ProjectDetailModal,
	})),
);

type ProjectCategory = "residential" | "commercial" | "hospitality";
type SortOption = "newest" | "oldest" | "a-z" | "z-a" | "featured";

const VALID_SORT_OPTIONS: readonly SortOption[] = [
	"newest",
	"oldest",
	"a-z",
	"z-a",
	"featured",
];

const VALID_CATEGORIES: readonly ProjectCategory[] = [
	"residential",
	"commercial",
	"hospitality",
];

function isValidSortOption(value: unknown): value is SortOption {
	return (
		typeof value === "string" &&
		VALID_SORT_OPTIONS.includes(value as SortOption)
	);
}

function isValidCategory(value: unknown): value is ProjectCategory {
	return (
		typeof value === "string" &&
		VALID_CATEGORIES.includes(value as ProjectCategory)
	);
}

const CATEGORIES = [
	{ id: "all", label: "All Projects" },
	{ id: "residential", label: "Residential" },
	{ id: "commercial", label: "Commercial" },
	{ id: "hospitality", label: "Hospitality" },
] as const;

const SORT_OPTIONS = [
	{ id: "newest", label: "Newest First" },
	{ id: "oldest", label: "Oldest First" },
	{ id: "a-z", label: "A to Z" },
	{ id: "z-a", label: "Z to A" },
	{ id: "featured", label: "Featured First" },
] as const;

const PROJECTS_PER_PAGE = 12;

// Search params schema for URL sync
const searchParamsSchema = z.object({
	category: z.enum(["residential", "commercial", "hospitality"]).optional(),
	search: z.string().optional(),
	sort: z
		.enum(["newest", "oldest", "a-z", "z-a", "featured"])
		.default("newest"),
});

type SearchParams = z.infer<typeof searchParamsSchema>;

export const Route = createFileRoute("/portfolio")({
	component: PortfolioComponent,
	validateSearch: searchParamsSchema,
	// Define search params as loader dependencies for SSR prefetch
	loaderDeps: ({ search }) => ({
		category: search.category,
		sort: search.sort,
	}),
	head: () => ({
		meta: [
			{ title: "Interior Design Portfolio | Designs by Maryam" },
			{
				name: "description",
				content:
					"Explore our award-winning interior design portfolio featuring residential, commercial, and hospitality projects across Bahrain and the Gulf region.",
			},
			{
				name: "keywords",
				content:
					"interior design, portfolio, residential, commercial, hospitality, Bahrain, Gulf design",
			},
			{
				property: "og:title",
				content: "Interior Design Portfolio | Designs by Maryam",
			},
			{
				property: "og:description",
				content:
					"Explore our award-winning interior design portfolio featuring residential, commercial, and hospitality projects.",
			},
			{ property: "og:type", content: "website" },
			{ property: "og:image", content: "/og-portfolio.jpg" },
		],
	}),
	// SSR prefetch for initial page load - improves LCP and SEO
	loader: async ({ context, deps }) => {
		const { queryClient } = context;

		// Prefetch initial page of infinite query based on URL search params
		await queryClient.prefetchInfiniteQuery(
			listProjectsInfiniteQueryOptions({
				category: deps.category,
				sort: deps.sort,
				limit: PROJECTS_PER_PAGE,
			}),
		);
	},
});

function PortfolioComponent() {
	const navigate = useNavigate({ from: "/portfolio" });
	const searchParams = Route.useSearch();

	// Local state for search input (debounced) and modal
	const [searchInput, setSearchInput] = useState(searchParams.search ?? "");
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null,
	);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const { slideUp, viewportOnce } = useSafeMotion();

	// Debounce search input (300ms)
	const [debouncedSearch, setDebouncedSearch] = useState(searchParams.search);
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchInput || undefined);
			// Update URL when search changes
			if (searchInput !== searchParams.search) {
				navigate({
					search: (prev: SearchParams) => ({
						...prev,
						search: searchInput || undefined,
					}),
					replace: true,
				});
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [searchInput, searchParams.search, navigate]);

	// Sync local search input with URL params on mount
	useEffect(() => {
		if (searchParams.search !== undefined) {
			setSearchInput(searchParams.search);
		}
	}, [searchParams.search]);

	// Infinite query for paginated projects - cache survives navigation
	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteQuery({
			...listProjectsInfiniteQueryOptions({
				category: searchParams.category,
				search: debouncedSearch,
				sort: searchParams.sort,
				limit: PROJECTS_PER_PAGE,
			}),
			staleTime: 5 * 60 * 1000, // 5 minutes
		});

	// Flatten all pages into a single project array
	const displayProjects = useMemo(
		() => data?.pages.flatMap((page) => page.data) ?? [],
		[data?.pages],
	);

	// Update URL params for category
	const handleCategoryChange = useCallback(
		(category: ProjectCategory | undefined) => {
			navigate({
				search: (prev: SearchParams) => ({
					...prev,
					category,
				}),
				replace: true,
			});
		},
		[navigate],
	);

	// Update URL params for sort
	const handleSortChange = useCallback(
		(sort: SortOption) => {
			navigate({
				search: (prev: SearchParams) => ({
					...prev,
					sort,
				}),
				replace: true,
			});
		},
		[navigate],
	);

	// Clear all filters
	const handleClearFilters = useCallback(() => {
		setSearchInput("");
		navigate({
			search: {
				sort: "newest",
			},
			replace: true,
		});
	}, [navigate]);

	// Load more projects using infinite query
	const handleLoadMore = useCallback(() => {
		if (!isFetchingNextPage && hasNextPage) {
			fetchNextPage();
		}
	}, [isFetchingNextPage, hasNextPage, fetchNextPage]);

	// Modal handlers
	const handleProjectClick = useCallback((projectId: string) => {
		setSelectedProjectId(projectId);
		setIsModalOpen(true);
	}, []);

	const handleModalClose = useCallback(() => {
		setIsModalOpen(false);
		setTimeout(() => setSelectedProjectId(null), 200);
	}, []);

	// Check if any filters are active
	const hasActiveFilters = useMemo(
		() =>
			searchParams.category !== undefined ||
			(searchParams.search && searchParams.search.length > 0) ||
			searchParams.sort !== "newest",
		[searchParams.category, searchParams.search, searchParams.sort],
	);

	return (
		<div className="min-h-screen bg-background">
			{/* Hero Section */}
			<section className="relative bg-gradient-to-br from-background via-muted/5 to-background py-20 md:py-32">
				{/* Subtle grid pattern overlay */}
				<div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(198_15%_85%_/_0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(198_15%_85%_/_0.3)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] dark:bg-[linear-gradient(to_right,hsl(198_15%_25%_/_0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(198_15%_25%_/_0.3)_1px,transparent_1px)]" />
				<div className="container relative mx-auto px-4 md:px-6">
					<motion.div
						className="mx-auto max-w-3xl text-center"
						initial={slideUp.initial}
						transition={slideUp.transition}
						viewport={viewportOnce}
						whileInView={slideUp.animate}
					>
						<h1 className="font-bold text-4xl tracking-tight md:text-5xl lg:text-6xl">
							Portfolio
						</h1>
						<p className="mt-4 text-lg text-muted-foreground md:text-xl">
							Explore my collection of interior design projects across
							residential, commercial, and hospitality spaces in Bahrain.
						</p>
					</motion.div>

					{/* Search and Sort Controls */}
					<div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
						{/* Search Input */}
						<div className="relative w-full max-w-md">
							<Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
							<input
								aria-label="Search projects"
								className="h-10 w-full rounded-md border border-input bg-background py-2 pr-10 pl-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
								onChange={(e) => setSearchInput(e.target.value)}
								placeholder="Search projects..."
								type="search"
								value={searchInput}
							/>
							{searchInput && (
								<button
									aria-label="Clear search"
									className="-translate-y-1/2 absolute top-1/2 right-3 text-muted-foreground hover:text-foreground"
									onClick={() => setSearchInput("")}
									type="button"
								>
									<X className="h-4 w-4" />
								</button>
							)}
						</div>

						{/* Sort Dropdown */}
						<Select
							onValueChange={(value) => {
								if (isValidSortOption(value)) {
									handleSortChange(value);
								}
							}}
							value={searchParams.sort}
						>
							<SelectTrigger className="w-[180px]">
								<SelectValue placeholder="Sort by" />
							</SelectTrigger>
							<SelectContent>
								{SORT_OPTIONS.map((option) => (
									<SelectItem key={option.id} value={option.id}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Category Filter */}
					<div className="mt-6 flex flex-wrap justify-center gap-2">
						{CATEGORIES.map((category) => {
							const isActive =
								category.id === "all"
									? searchParams.category === undefined
									: searchParams.category === category.id;
							return (
								<Button
									aria-pressed={isActive}
									className="transition-all"
									key={category.id}
									onClick={() => {
										if (category.id === "all") {
											handleCategoryChange(undefined);
										} else if (isValidCategory(category.id)) {
											handleCategoryChange(category.id);
										}
									}}
									size="lg"
									variant={isActive ? "brand" : "outline"}
								>
									{category.label}
								</Button>
							);
						})}
					</div>

					{/* Active Filters Chips */}
					{hasActiveFilters && (
						<div className="mt-4 flex flex-wrap items-center justify-center gap-2">
							<span className="text-muted-foreground text-sm">
								Active filters:
							</span>
							{searchParams.category && (
								<span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-primary text-sm">
									{
										CATEGORIES.find((c) => c.id === searchParams.category)
											?.label
									}
									<button
										aria-label={`Remove ${searchParams.category} filter`}
										className="ml-1 hover:text-primary/80"
										onClick={() => handleCategoryChange(undefined)}
										type="button"
									>
										<X className="h-3 w-3" />
									</button>
								</span>
							)}
							{searchParams.search && (
								<span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-primary text-sm">
									"{searchParams.search}"
									<button
										aria-label="Remove search filter"
										className="ml-1 hover:text-primary/80"
										onClick={() => setSearchInput("")}
										type="button"
									>
										<X className="h-3 w-3" />
									</button>
								</span>
							)}
							{searchParams.sort !== "newest" && (
								<span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-primary text-sm">
									{SORT_OPTIONS.find((s) => s.id === searchParams.sort)?.label}
									<button
										aria-label="Reset sort order"
										className="ml-1 hover:text-primary/80"
										onClick={() => handleSortChange("newest")}
										type="button"
									>
										<X className="h-3 w-3" />
									</button>
								</span>
							)}
							<Button
								className="ml-2"
								onClick={handleClearFilters}
								size="sm"
								variant="ghost"
							>
								Clear all
							</Button>
						</div>
					)}
				</div>
			</section>

			{/* Projects Grid Section */}
			<section className="py-16 md:py-24">
				<div className="container mx-auto px-4 md:px-6">
					{/* Results count */}
					{data?.pages[0]?.pagination?.total !== undefined && !isLoading && (
						<p className="mb-8 text-center text-muted-foreground">
							Showing {displayProjects.length} of{" "}
							{data.pages[0].pagination.total} projects
						</p>
					)}

					<ProjectsGrid
						emptyMessage="No projects found in this category. Check back soon!"
						hasMore={hasNextPage ?? false}
						isLoading={isLoading}
						isLoadingMore={isFetchingNextPage}
						onLoadMore={handleLoadMore}
						onProjectClick={handleProjectClick}
						projects={displayProjects}
					/>
				</div>
			</section>

			{/* Project Detail Modal - lazy loaded */}
			<Suspense fallback={null}>
				<ProjectDetailModal
					isOpen={isModalOpen}
					onClose={handleModalClose}
					projectId={selectedProjectId}
				/>
			</Suspense>
		</div>
	);
}
